/* global grecaptcha */

import {channel} from "redux-saga";
import {useCallback, useLayoutEffect, useRef} from "react";
import styled from "styled-components";

import {useStoreProfile} from '../store';
import Overlay from "../components/overlay";
import {themecolors} from "../utils/themecolors";
import useModal from ".";

const SITE_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY;
const SCRIPT_ID = 'recaptcha';

function debug(...args) {
    if (import.meta.env.VITE_DEBUG) {
        // eslint-disable-next-line no-console
        console.log(...args);
    }
}

let loaded = false;
const loadChannel = channel();
window.onLoadRecaptcha = () => {
    loadChannel.close();
    loaded = true;
};

const loadTurnstileScript = () => {
    if (document.getElementById(SCRIPT_ID)) return Promise.resolve();

    return new Promise((resolve, reject) => {
        const script = document.createElement("script");
        script.id = SCRIPT_ID;
        script.src = "https://challenges.cloudflare.com/turnstile/v0/api.js";
        script.async = true;
        script.defer = true;
        script.onload = resolve;
        script.onerror = reject;
        document.body.appendChild(script);
    });
};

const resultChannel = channel();

const Recaptcha = styled(({className, onClose}) => {
    const ref = useRef(null);
    const settledRef = useRef(false);
    const {theme} = useStoreProfile();
    const themeRef = useRef(theme);
    const done = useCallback((value) => {
        if (!settledRef.current) {
            settledRef.current = true;
            if (typeof onClose === 'function') {
                onClose();
            }
            resultChannel.put(value);
        }
    }, [onClose]);
    useLayoutEffect(() => {
        loadTurnstileScript()
            .then(() => {
                if (!ref.current) {
                    debug("turnstile cancelled");
                    return;
                }
                window.turnstile.render(ref.current, {
                    sitekey: SITE_KEY,
                    theme,
                    callback: (token) => done({ token }),
                    "expired-callback": () => done({ error: new Error("expired") }),
                });
            })
            .catch((error) => {
                done({ error });
            });

        return () => done({ error: new Error("cancelled") });
    }, [done]);

    return (
        <Overlay onClose={() => done({ error: new Error('cancelled') })}>
            <div className={className}>
                <p>Beep boop?</p>
                <div ref={ref} />
            </div>
        </Overlay>
    );
})`
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  padding: 20px;
  border-radius: 12px;
  text-align: center;
  ${({theme}) => `
    color: ${theme.main.color};
    background-color: ${theme.color.name === 'dark' ? themecolors.dark_background_1 : '#fff'};
    border: ${theme.color.name === 'dark' ? `1px solid ${themecolors.dark_background_5}` : 'none'};
  `}
  p {
    font-weight: 500;
    font-size: 18px;
    padding-bottom: 12px;
  }
`;

export default Recaptcha;

export const useRecaptcha = () => {
    const modal = useModal();
    return useCallback(() => new Promise((resolve, reject) => {
        modal.push('Recaptcha');
        resultChannel.take(({ token, error }) => {
            if (token) {
                resolve(token);
            } else {
                reject(error);
            }
        });
    }), [modal]);
};
